import dayjs from 'dayjs';
import { ChangeEvent, useCallback, useContext, useState } from 'react';

import {
	Badge,
	Box,
	Button,
	Flex,
	Group,
	LoadingOverlay,
	Pagination,
	ScrollArea,
	Table,
	Text,
} from '@mantine/core';
import { randomId } from '@mantine/hooks';

import { useQuery } from '@tanstack/react-query';

import { AppContext } from '@/contexts/AppContext/AppContextProvider.tsx';

import { NewRateForm } from '@/entities/Rate/NewRateForm.tsx';

import { ratingApi } from '@/shared/api/rating.ts';
import { chunk } from '@/shared/helpers/utils.ts';
import { SearchInput } from '@/shared/uikit/components/input';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

const RatedBookings = () => {
	const [activePage, setPage] = useState(1);
	const [searchValue, setSearchValue] = useState('');
	const { admin } = useContext(AppContext);
	const [isShowNewRateForm, setIsShowNewRateForm] = useState(false);

	const { data: ratedBookings, isLoading } = useQuery({
		queryKey: ['ratedBookings'],
		queryFn: () => ratingApi.getRatedBookings(),
	});

	const handleSort = useCallback(
		() => ratedBookings?.objects.sort((a, b) => dayjs(b?.start).unix() - dayjs(a?.start).unix()),
		[ratedBookings],
	);

	const handleSearch = (evt: ChangeEvent<HTMLInputElement>) =>
		setSearchValue(evt.currentTarget?.value.toLowerCase().trim());

	const filtered = handleSort()?.filter((item) =>
		item?.full_name?.toLowerCase().includes(searchValue),
	);

	const chunked = chunk(filtered ?? [], 10);

	const rows = chunked[activePage - 1]?.map((booking) => (
		<Table.Tr key={randomId()}>
			<Table.Td>
				<Text size="md" fw={600} lts="normal">
					{booking?.full_name}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{dayjs(booking?.start).format('DD MMMM YYYY HH:mm')}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{dayjs(booking?.finish).format('DD MMMM YYYY HH:mm')}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{dayjs(booking?.access_in).format('DD MMMM YYYY HH:mm')}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{dayjs(booking?.access_out).format('DD MMMM YYYY HH:mm')}
				</Text>
			</Table.Td>
			<Table.Td ta="center">
				<Badge variant="light" color={booking?.captured_by_camera ? 'green' : 'red'}>
					<Text size="md" fw={600}>
						{booking?.captured_by_camera ? 'Да' : 'Нет'}
					</Text>
				</Badge>
			</Table.Td>
			<Table.Td ta="center">
				<Text size="md" c="dimmed">
					{booking?.value}
				</Text>
			</Table.Td>
		</Table.Tr>
	));

	return (
		<LayoutRow
			title={{
				title: { children: 'Оценённые бронирования', size: 'h4', fw: 800 },
			}}
			type="once"
			style={{ height: 'calc(100vh - 116px)' }}
		>
			<Box pos="relative" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
				<LoadingOverlay visible={isLoading} zIndex={1000} overlayProps={{ radius: 2, blur: 2 }} />
				{isShowNewRateForm ? (
					<NewRateForm setShowForm={setIsShowNewRateForm}>
						<Button variant="light" onClick={() => setIsShowNewRateForm(false)}>
							<Text fw={500}>Отменить</Text>
						</Button>
					</NewRateForm>
				) : (
					<Group>
						<SearchInput
							w={530}
							value={searchValue}
							onChange={handleSearch}
							placeholder="Поиск по ФИО сотрудника"
						/>
						{admin && (
							<Button onClick={() => setIsShowNewRateForm(true)}>
								<Text fw={500}>Создать коэффициент на дату</Text>
							</Button>
						)}
					</Group>
				)}
				{chunked.length ? (
					<>
						<ScrollArea h={'100dvh'} pr={'md'} type={'auto'} scrollbarSize={12} offsetScrollbars>
							<Table verticalSpacing="md" stickyHeader>
								<Table.Thead>
									<Table.Tr>
										<Table.Th>
											<Text size="md" c="dimmed">
												ФИО
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Начало
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Конец
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Дата въезда
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Дата выезда
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Пойман на камеру
											</Text>
										</Table.Th>
										<Table.Th ta="center">
											<Text size="md" c="dimmed">
												Значение
											</Text>
										</Table.Th>
									</Table.Tr>
								</Table.Thead>
								<Table.Tbody>{rows}</Table.Tbody>
							</Table>
						</ScrollArea>
						{chunked.length > 1 && (
							<Pagination total={chunked.length} value={activePage} onChange={setPage} mt="sm" />
						)}
					</>
				) : (
					<Flex direction="column" align="center" justify="center" h={'calc(100vh - 300px)'}>
						<Text fz={18} fw={700}>
							Пока ничего нет 😔
						</Text>
					</Flex>
				)}
			</Box>
		</LayoutRow>
	);
};

export default RatedBookings;
